<script lang="ts">
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import type { SalesRep, RetailerSalesRepContact } from "@shared/types/SalesRep";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import DistributorsStore from "@shared/svelte/Stores/DistributorsStore";
  import type { SharedOrder } from "@shared/types/Order.js";

  export let show: boolean;
  export let showEditSalesRepModal: () => void = () => {};
  export let closeModal: (clearErrors: boolean | null) => void = () => {};
  export let editSalesRep: (
    retailerSalesRepContact?: RetailerSalesRepContact,
    confirm?: boolean
  ) => Promise<void>;
  export let editingSalesRep: SalesRep;
  export let suggestedSalesRep: SalesRep;
  export let order: SharedOrder;
  export let addRepToOrder: (salesRep: SalesRep) => void = null;
  export let removeRepFromOrder: (salesRep: SalesRep) => void = null;
  export let reloadOnSuccess = false;

  $: selectedRep = "existing";
  let mismatchName: boolean;
  let mismatchEmail: boolean;
  let mismatchPhone: boolean;

  $: {
    mismatchName = editingSalesRep?.name?.toLowerCase() != suggestedSalesRep?.name?.toLowerCase();
    mismatchEmail = editingSalesRep?.contact_email != suggestedSalesRep?.contact_email;
    mismatchPhone = editingSalesRep?.contact_phone != suggestedSalesRep?.contact_phone;
  }

  const confirmSalesRep = async () => {
    if (selectedRep === "entered")
      await editSalesRep(editingSalesRep.retailer_sales_rep_contact, true);
    else await connectSalesRep();
    show = false;
  };

  const connectSalesRep = async () => {
    try {
      const res = await HttpPromise({
        method: "POST",
        url: `/api/retailer/distributors/${suggestedSalesRep.distributor_id}/sales_reps/${suggestedSalesRep.id}/assignments`,
        data: { retailer_direct_add: false, order_id: order?.id, reject_if_exists: true },
      });
      handleSuccess();
    } catch (errorRes) {
      handleFailure(errorRes);
    }
  };

  const handleFailure = (errorRes: any) => {
    const errorMessages = errorRes.errors.messages;
    if (
      errorMessages.includes(
        "Sales rep with this phone number and/or email is already assigned to you"
      )
    ) {
      Flash.now({
        type: "error",
        message: "Contact already added to distributor",
        header: "Failure",
      });
    } else {
      Flash.now({ type: "error", message: "Failed to connect existing rep", header: "Error" });
    }

    closeModal(false);
  };

  const handleSuccess = () => {
    show = false;
    // close SalesRepModal and clear it!
    deleteRepAssignment();
    addRepToOrder(suggestedSalesRep);
    closeModal(true);

    // Replace editing sales rep by suggested
    DistributorsStore.updateDistributorSalesRep(
      suggestedSalesRep.distributor_id,
      editingSalesRep.id,
      {
        ...suggestedSalesRep,
        has_email_notification_errors: false,
        has_phone_notification_errors: false,
      }
    );
    Flash.now({ type: "success", message: "Distributor changes saved", header: "Success" });
    if (reloadOnSuccess) window.location.reload();
  };

  const deleteRepAssignment = async () => {
    try {
      removeRepFromOrder(editingSalesRep);
      await HttpPromise({
        method: "DELETE",
        url: `/api/retailer/distributors/${suggestedSalesRep.distributor_id}/sales_reps/${editingSalesRep.id}/assignments`,
        data: {
          order_id: order?.id,
        },
      });
    } catch (error) {
      Flash.now({ type: "error", message: "Failed to connect existing rep", header: "Error" });
    }
  };

</script>

<style>
  .top-margin {
    margin-top: 0.1rem;
  }

</style>

<Modal closeOnBackgroundClick={true} bind:show size={"medium"} fizzModal={true}>
  <div class="fizz-inset-squish-24">
    <header class="fizz-flex-row fizz-align-content-start fizz-stack-16">
      <div>
        <h4 class="fizz-heading-4 fizz-stack-4">Confirm your contact</h4>
        <p>
          We found a similar contact in our database. Review our suggestion and select the contact
          you wish to use.
        </p>
      </div>
      <div on:click={() => (show = false)}>
        <Icon type="X" extraClassNames="fizz-text-brand cursor-pointer" height="14" width="14" />
      </div>
    </header>
    <div class="fizz-stack-32 fizz-bordered">
      <label
        class="fizz-flex-row-8 fizz-inset-square-16 fizz-align-content-start fizz-border-bottom"
        for="suggested_rep"
      >
        <input
          type="radio"
          class="fizz-radio-button top-margin"
          id="suggested_rep"
          name="suggested_rep"
          value={"existing"}
          bind:group={selectedRep}
        />
        <span>
          <h4 class="fizz-text-strong fizz-stack-4">Suggested contact:</h4>
          <div>
            <p class="capitalize" class:fizz-text-negative={mismatchName}>
              {suggestedSalesRep.name}
            </p>
            <p class:fizz-text-negative={mismatchEmail}>{suggestedSalesRep.contact_email}</p>
            <p class:fizz-text-negative={mismatchPhone}>{suggestedSalesRep.contact_phone || ""}</p>
          </div>
        </span>
      </label>
      <label
        class="fizz-flex-row-8 fizz-inset-square-16 fizz-align-content-start"
        for="entered_rep"
      >
        <input
          type="radio"
          class="fizz-radio-button top-margin"
          id="entered_rep"
          name="entered_rep"
          value={"entered"}
          bind:group={selectedRep}
        />
        <span>
          <h4 class="fizz-text-strong fizz-stack-4">You entered:</h4>
          <div>
            <p class="capitalize">{editingSalesRep.name}</p>
            {#if editingSalesRep.contact_email}
              <p>{editingSalesRep.contact_email}</p>
            {/if}
            {#if editingSalesRep.contact_phone}
              <p>{editingSalesRep.contact_phone}</p>
            {/if}
          </div>
        </span>
      </label>
    </div>
    <div class="fizz-flex-row fizz-justify-end">
      <button class="fizz-button" on:click={showEditSalesRepModal}>Edit</button>
      <button class="fizz-button-secondary" on:click={confirmSalesRep}>Confirm</button>
    </div>
  </div>
</Modal>
